import React, { ErrorInfo, Suspense } from 'react'

import { addReactError } from '@datadog/browser-rum-react'
import { useQueryErrorResetBoundary } from '@tanstack/react-query'
import { Outlet, createFileRoute } from '@tanstack/react-router'
import { ErrorBoundary } from 'react-error-boundary'

import { useAuth, withAuthenticationRequired } from '@enechain/auth-js'
import { Error } from '@enechain/ecloud-designsystem'

import { TermAgreement } from '~/components/features/term'
import FullLoadingView from '~/components/layout/FullLoadingView'
import { MenuLayout } from '~/components/layout/Menu'
import { I18nProvider } from '~/i18n/i18nProvider'
import { GTMDataLayerProvider } from '~/provider/GTMDataLayerProvider'

const Main = (): JSX.Element => {
  const { reset } = useQueryErrorResetBoundary()
  const { logout } = useAuth()
  const fallbackComponent = ({
    error,
    resetErrorBoundary,
  }: {
    error: Error
    resetErrorBoundary: () => void
  }): React.ReactElement => (
    <Error
      error={{ message: error.message }}
      logout={(): void => {
        logout({ returnTo: window.location.origin })
      }}
      resetErrorBoundary={resetErrorBoundary}
    />
  )

  const logError = (error: Error, info: ErrorInfo): void => {
    addReactError(error, info)
  }

  return (
    <ErrorBoundary
      fallbackRender={fallbackComponent}
      onReset={reset}
      onError={logError}
    >
      <Suspense fallback={<FullLoadingView />}>
        <GTMDataLayerProvider>
          <I18nProvider>
            <MenuLayout>
              <Outlet />
              <TermAgreement />
            </MenuLayout>
          </I18nProvider>
        </GTMDataLayerProvider>
      </Suspense>
    </ErrorBoundary>
  )
}

export const Route = createFileRoute('/_authenticated')({
  component: withAuthenticationRequired(Main, {
    onRedirecting: () => <FullLoadingView />,
    returnTo: window.location.href,
    onLoading: () => <FullLoadingView />,
  }),
})
